import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlinePlus, HiOutlineKey, HiOutlineBan, HiOutlineCheck, HiOutlinePencil } from 'react-icons/hi';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showResetPw, setShowResetPw] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', isSales: false, isMarketing: false, isPartnerManager: false, calendlyUrl: '' });
  const [editData, setEditData] = useState({ isSales: false, isMarketing: false, isPartnerManager: false, calendlyUrl: '', displayName: '' });
  const [newPassword, setNewPassword] = useState('');
  const [search, setSearch] = useState('');
  const { addToast } = useToast();

  const loadUsers = () => {
    setLoading(true);
    api.get('/admin/users').then(({ data }) => {
      setUsers(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/users', newUser);
      addToast('User created successfully');
      setShowCreate(false);
      setNewUser({ email: '', password: '', displayName: '', isSales: false, isMarketing: false, isPartnerManager: false, calendlyUrl: '' });
      loadUsers();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to create user', 'error');
    }
  };

  const handleEdit = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/roles`, editData);
      addToast('User roles updated');
      setShowEdit(null);
      loadUsers();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to update roles', 'error');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 8) {
      addToast('Password must be at least 8 characters', 'error');
      return;
    }
    try {
      await api.post(`/admin/users/${userId}/reset-password`, { newPassword });
      addToast('Password reset successfully');
      setShowResetPw(null);
      setNewPassword('');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to reset password', 'error');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      const newStatus = !user.isActive;
      await api.put(`/admin/users/${user.id}/status`, { isActive: newStatus });
      addToast(`User ${newStatus ? 'enabled' : 'disabled'}`);
      loadUsers();
    } catch (err) {
      addToast('Failed to update status', 'error');
    }
  };

  const openEdit = (user) => {
    setEditData({
      isSales: user.isSales,
      isMarketing: user.isMarketing,
      isPartnerManager: user.isPartnerManager,
      calendlyUrl: user.partnerManager?.calendlyUrl || '',
      displayName: user.dashboardProfile?.displayName || ''
    });
    setShowEdit(user.id);
  };

  const getRoleBadges = (user) => {
    const badges = [];
    if (user.isMarketing) badges.push({ label: 'Marketing', color: 'bg-purple-100 text-purple-800' });
    if (user.isSales) badges.push({ label: 'Sales', color: 'bg-blue-100 text-blue-800' });
    if (user.isPartnerManager) badges.push({ label: 'PM', color: 'bg-amber-100 text-amber-800' });
    return badges;
  };

  const filtered = users.filter(u => {
    const name = u.dashboardProfile?.displayName || '';
    const email = u.email || '';
    return name.toLowerCase().includes(search.toLowerCase()) ||
      email.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-500">Manage internal team accounts (CS, Sales, Partner Managers)</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700">
          <HiOutlinePlus className="w-5 h-5" /> Add User
        </button>
      </div>

      <input
        type="text"
        placeholder="Search users..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-v2-blue outline-none"
      />

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Create Team Member</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" required value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
                <input type="text" required minLength={8} value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input type="text" required value={newUser.displayName} onChange={e => setNewUser({...newUser, displayName: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={newUser.isMarketing} onChange={e => setNewUser({...newUser, isMarketing: e.target.checked})}
                      className="rounded border-gray-300 text-v2-blue focus:ring-v2-blue" />
                    <span className="text-sm">Internal Marketing</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={newUser.isSales} onChange={e => setNewUser({...newUser, isSales: e.target.checked})}
                      className="rounded border-gray-300 text-v2-blue focus:ring-v2-blue" />
                    <span className="text-sm">Sales</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={newUser.isPartnerManager} onChange={e => setNewUser({...newUser, isPartnerManager: e.target.checked})}
                      className="rounded border-gray-300 text-v2-blue focus:ring-v2-blue" />
                    <span className="text-sm">Partner Manager</span>
                  </label>
                </div>
              </div>

              {newUser.isPartnerManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calendly URL</label>
                  <input type="url" value={newUser.calendlyUrl} onChange={e => setNewUser({...newUser, calendlyUrl: e.target.value})}
                    placeholder="https://calendly.com/your-link"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 bg-v2-blue text-white py-2 rounded-lg font-medium hover:bg-blue-700">Create</button>
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-gray-300 py-2 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit roles modal */}
      {showEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowEdit(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">Edit Roles</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input type="text" value={editData.displayName} onChange={e => setEditData({...editData, displayName: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Roles</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editData.isMarketing} onChange={e => setEditData({...editData, isMarketing: e.target.checked})}
                      className="rounded border-gray-300 text-v2-blue focus:ring-v2-blue" />
                    <span className="text-sm">Internal Marketing</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editData.isSales} onChange={e => setEditData({...editData, isSales: e.target.checked})}
                      className="rounded border-gray-300 text-v2-blue focus:ring-v2-blue" />
                    <span className="text-sm">Sales</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editData.isPartnerManager} onChange={e => setEditData({...editData, isPartnerManager: e.target.checked})}
                      className="rounded border-gray-300 text-v2-blue focus:ring-v2-blue" />
                    <span className="text-sm">Partner Manager</span>
                  </label>
                </div>
              </div>

              {editData.isPartnerManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Calendly URL</label>
                  <input type="url" value={editData.calendlyUrl} onChange={e => setEditData({...editData, calendlyUrl: e.target.value})}
                    placeholder="https://calendly.com/your-link"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={() => handleEdit(showEdit)} className="flex-1 bg-v2-blue text-white py-2 rounded-lg font-medium hover:bg-blue-700">Save</button>
                <button onClick={() => setShowEdit(null)} className="flex-1 border border-gray-300 py-2 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Roles</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Assets</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No users found.</td>
                </tr>
              ) : filtered.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{user.dashboardProfile?.displayName || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {getRoleBadges(user).map(b => (
                        <span key={b.label} className={`px-2 py-0.5 text-xs rounded-full font-medium ${b.color}`}>{b.label}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{user.dashboardProfile?._count?.generatedAssets || 0}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {user.isActive ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-center">
                      <button onClick={() => openEdit(user)} className="text-gray-400 hover:text-v2-blue" title="Edit roles">
                        <HiOutlinePencil className="w-5 h-5" />
                      </button>
                      <button onClick={() => { setShowResetPw(user.id); setNewPassword(''); }} className="text-gray-400 hover:text-v2-blue" title="Reset password">
                        <HiOutlineKey className="w-5 h-5" />
                      </button>
                      <button onClick={() => handleToggleStatus(user)} className={`${user.isActive ? 'text-gray-400 hover:text-red-600' : 'text-gray-400 hover:text-green-600'}`} title={user.isActive ? 'Disable' : 'Enable'}>
                        {user.isActive ? <HiOutlineBan className="w-5 h-5" /> : <HiOutlineCheck className="w-5 h-5" />}
                      </button>
                    </div>
                    {showResetPw === user.id && (
                      <div className="mt-2 flex gap-2">
                        <input type="text" placeholder="New password (min 8)" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded text-sm flex-1 focus:ring-2 focus:ring-v2-blue outline-none" />
                        <button onClick={() => handleResetPassword(user.id)} className="px-3 py-1 bg-v2-blue text-white rounded text-sm hover:bg-blue-700">Reset</button>
                        <button onClick={() => setShowResetPw(null)} className="px-3 py-1 border rounded text-sm hover:bg-gray-50">Cancel</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
